import { isEqual } from 'lodash';

import { createEditor } from 'com/Editor';
import { apiAddDeck, apiGetDeck, apiUpdateDeck } from 'core/api';

import Form from './Form';

const editor = createEditor<null, TDeck, TDeckUpdate>({
  mode: 'withoutContainer',
  transform,
  Form,
  getParentUid: () => '%fakeParentId%',
  onValidate: validate,
  genNewEntity,
  // oxlint-disable-next-line typescript-eslint/require-await
  apiGetContainer: async () => null,
  apiGetEntity: apiGetDeck,
  apiUpdateEntity: apiUpdateDeck,
  apiAddEntity: (_containerId, deck) => apiAddDeck(deck),
});

function validate(deck: TDeckUpdate | TDeck) {
  if (isEqual(deck, genNewEntity())) {
    return { isEmpty: true, error: null };
  }

  if (!deck.name) {
    return { isEmpty: false, error: 'Name is empty' };
  }

  if (!deck.exercises.length) {
    return { isEmpty: false, error: 'Choose at least one exercise' };
  }

  return { isEmpty: false, error: null };
}

function genNewEntity(): TDeckUpdate {
  return {
    lang: null,
    name: '',
    templateType: 'default',
    exercises: Object.values(TExercise),
    definitionLangs: ['none'],
  };
}

function transform(
  deck: TDeck | TDeckUpdate,
  field: string,
  subField: string | undefined,
  value: string
): TDeckUpdate {
  switch (field) {
    case 'exercises':
      return {
        ...deck,
        exercises: deck.exercises.includes(subField as TExercise)
          ? deck.exercises.filter(exe => exe !== subField)
          : [...deck.exercises, subField as TExercise],
      };
    case 'definitionLangs': {
      const isOn = deck.definitionLangs.includes(subField as TLangAndNone);
      return {
        ...deck,
        definitionLangs:
          subField === 'none' && !isOn
            ? ['none']
            : isOn
              ? deck.definitionLangs.filter(lang => lang !== subField)
              : [...deck.definitionLangs, subField as TLangAndNone].filter(
                  v => v !== 'none'
                ),
      };
    }
    case 'lang':
      return {
        ...deck,
        lang: value === 'null' ? null : (value as TLang),
      };
    case 'name':
      return { ...deck, name: value };

    default:
      return { ...deck, [field as keyof TDeck]: value ? value : null };
  }
}

export const DeckEditorProvider = editor.EditorProvider;
export const useDeckEditor = function useDeckEditor() {
  const { createEntity, editEntity } = editor.useEditor();
  return {
    createDeck: () => createEntity('%fakeParentId%'),
    editDeck: editEntity,
  };
};
